import {
  type AircraftBuildResponse,
  type AircraftCatalogModel,
  type GltfDoc,
} from "../../api/aircraft";

import {
  buildVspPreview,
  fetchVspModels,
  fetchVspParameters,
  fetchVspPreviewMetadata,
  fetchVspPreviewModel,
  fetchVspPreviewSurfaces,
} from "./x57VspApi";
import {
  parseX57SurfaceMetadata,
  parseX57SurfaceModel,
  type X57SurfaceMetadata,
  type X57SurfaceModel,
} from "./x57SurfaceModel";
import {
  isX57MainWingName,
  selectX57MainWingParameters,
  type X57EditableParameter,
} from "./x57VspParameters";
import {
  X57_DEFAULT_MODEL_NAME,
  X57_DEFAULT_VSP_SOURCE,
  X57_VSP_SOURCES,
  type X57VspSource,
} from "./x57VspSources";

export {
  selectX57MainWingParameters,
  type X57EditableParameter,
} from "./x57VspParameters";
export {
  X57_DEFAULT_MODEL_NAME,
  X57_DEFAULT_VSP_SOURCE,
  X57_VSP_SOURCES,
  type X57VspSource,
} from "./x57VspSources";

export type X57ComponentRole =
  | "main-wing"
  | "fuselage"
  | "empennage"
  | "nacelle"
  | "pylon"
  | "propeller"
  | "actuator-disk"
  | "other";

export interface X57DisplayMaterial {
  group: X57ComponentRole;
  color: string;
  opacity: number;
  transparent: boolean;
}

export interface X57DisplayComponent {
  id: string;
  name: string;
  role: X57ComponentRole;
  positions: Float32Array;
  indices: Uint32Array;
  material: Readonly<X57DisplayMaterial>;
}

export interface X57VspAircraft {
  delivery: "canonical-server-preview";
  modelName: typeof X57_DEFAULT_MODEL_NAME;
  source: X57VspSource;
  sourceLabel: string;
  catalogKey: string;
  build: AircraftBuildResponse;
  components: X57DisplayComponent[];
  surfaceModel: X57SurfaceModel;
  surfaceMetadata: X57SurfaceMetadata;
  editableParameters: X57EditableParameter[];
  parameterOverrides: Readonly<Record<string, number>>;
  warnings: string[];
}

const MATERIALS: Readonly<Record<X57ComponentRole, X57DisplayMaterial>> = {
  "main-wing": { group: "main-wing", color: "#8eb8d8", opacity: 1, transparent: false },
  fuselage: { group: "fuselage", color: "#c8d1da", opacity: 1, transparent: false },
  empennage: { group: "empennage", color: "#9fb8cc", opacity: 1, transparent: false },
  nacelle: { group: "nacelle", color: "#70879a", opacity: 1, transparent: false },
  pylon: { group: "pylon", color: "#7f98ac", opacity: 1, transparent: false },
  propeller: { group: "propeller", color: "#d6aa68", opacity: 0.9, transparent: true },
  "actuator-disk": { group: "actuator-disk", color: "#63c5da", opacity: 0.2, transparent: true },
  other: { group: "other", color: "#91a1ae", opacity: 1, transparent: false },
};

function classifyComponent(name: string): X57ComponentRole {
  const key = name.toLowerCase();
  if (isX57MainWingName(name)) return "main-wing";
  if (key.includes("propdisc") || key.includes("prop_disc")) return "actuator-disk";
  if (key.includes("blade") || (key.includes("prop") && !key.includes("pod"))) return "propeller";
  if (key.includes("nacelle")) return "nacelle";
  if (key.includes("pylon")) return "pylon";
  if (key.includes("horizontal") || key.includes("vertical") || key.includes("tail")) return "empennage";
  if (key.includes("fuselage") || key.includes("gearpod") || key.includes("gear_pod")) return "fuselage";
  return "other";
}

function base64Buffer(uri: string): ArrayBuffer {
  const comma = uri.indexOf(",");
  if (!uri.startsWith("data:") || comma < 0 || !uri.slice(0, comma).includes(";base64")) {
    throw new Error("X-57 aircraft bundle must use the embedded base64 glTF contract");
  }
  const binary = atob(uri.slice(comma + 1));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes.buffer;
}

function uintIndices(buffer: ArrayBuffer, offset: number, count: number, componentType: number): Uint32Array {
  if (componentType === 5125) return new Uint32Array(new Uint32Array(buffer, offset, count));
  if (componentType === 5123) return Uint32Array.from(new Uint16Array(buffer, offset, count));
  if (componentType === 5121) return Uint32Array.from(new Uint8Array(buffer, offset, count));
  throw new Error(`Unsupported glTF index component type ${componentType}`);
}

export function decodeX57DisplayComponents(
  doc: GltfDoc,
  buildComponents: AircraftBuildResponse["components"],
): X57DisplayComponent[] {
  const bufferInfo = doc.buffers[0];
  if (!bufferInfo) throw new Error("X-57 aircraft bundle has no embedded buffer");
  const buffer = base64Buffer(bufferInfo.uri);
  return doc.meshes.map((mesh, index) => {
    const primitive = mesh.primitives[0];
    if (!primitive) throw new Error(`X-57 mesh ${index} has no primitive`);
    const positionAccessor = doc.accessors[primitive.attributes.POSITION];
    const indexAccessor = doc.accessors[primitive.indices];
    if (!positionAccessor || !indexAccessor) throw new Error(`X-57 mesh ${index} has incomplete accessors`);
    const positionView = doc.bufferViews[positionAccessor.bufferView];
    const indexView = doc.bufferViews[indexAccessor.bufferView];
    if (!positionView || !indexView) throw new Error(`X-57 mesh ${index} has incomplete buffer views`);
    const positions = new Float32Array(new Float32Array(
      buffer,
      positionView.byteOffset ?? 0,
      positionAccessor.count * 3,
    ));
    const indices = uintIndices(
      buffer,
      indexView.byteOffset ?? 0,
      indexAccessor.count,
      indexAccessor.componentType,
    );
    const metadata = buildComponents[index];
    const name = metadata?.name ?? mesh.name ?? `component_${index}`;
    const role = classifyComponent(name);
    return {
      id: metadata?.geom_id ?? `${name}_${index}`,
      name,
      role,
      positions,
      indices,
      material: MATERIALS[role],
    };
  });
}

export function resolveX57CatalogKey(models: AircraftCatalogModel[]): string | null {
  for (const source of X57_VSP_SOURCES) {
    if (models.some((model) => model.key === source.catalogKey)) return source.catalogKey;
  }
  const inferred = models.find((model) => /x[-_ ]?57|maxwell/i.test(`${model.key} ${model.label}`));
  return inferred?.key ?? null;
}
function sourceForCatalogKey(key: string): X57VspSource | undefined {
  return X57_VSP_SOURCES.find((source) => source.catalogKey === key);
}
export interface X57BridgeDependencies {
  fetchCatalog: typeof fetchVspModels;
  build: typeof buildVspPreview;
  fetchParms: typeof fetchVspParameters;
  fetchModel: typeof fetchVspPreviewModel;
  fetchSurfaces: typeof fetchVspPreviewSurfaces;
  fetchMetadata: typeof fetchVspPreviewMetadata;
}
const DEFAULT_DEPENDENCIES: X57BridgeDependencies = {
  fetchCatalog: fetchVspModels,
  build: buildVspPreview,
  fetchParms: fetchVspParameters,
  fetchModel: fetchVspPreviewModel,
  fetchSurfaces: fetchVspPreviewSurfaces,
  fetchMetadata: fetchVspPreviewMetadata,
};
function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === "AbortError";
}
export async function loadX57VspAircraft(options: {
  projectId?: string;
  resolution?: number;
  dependencies?: X57BridgeDependencies;
  source?: X57VspSource;
  catalogKey?: string;
  parameterOverrides?: Readonly<Record<string, number>>;
  signal?: AbortSignal;
} = {}): Promise<X57VspAircraft> {
  if (options.source && options.catalogKey && options.source.catalogKey !== options.catalogKey) {
    throw new Error("X-57 source and catalogKey select different VSP models");
  }
  const requestedKey = options.source?.catalogKey ?? options.catalogKey;
  const requestedSource = options.source ?? (requestedKey ? sourceForCatalogKey(requestedKey) : undefined);
  if (requestedKey && !requestedSource) throw new Error(`Unknown X-57 VSP source: ${requestedKey}`);
  const parameterOverrides = { ...options.parameterOverrides };
  if (!options.projectId?.trim()) throw new Error("A Thread project id is required to load canonical VSP geometry");
  const projectId = options.projectId;
  const dependencies = options.dependencies ?? DEFAULT_DEPENDENCIES;
  const requestOptions = { signal: options.signal };
  const catalog = await dependencies.fetchCatalog(projectId, requestOptions);
  const catalogKey = requestedKey ?? resolveX57CatalogKey(catalog.models);
  if (!catalogKey) {
    throw new Error("X-57 is not in the VSP catalog; configure THREAD_VSP_FLEET_DIR with the Airshow models");
  }
  if (!catalog.models.some((model) => model.key === catalogKey)) {
    throw new Error(`Selected X-57 VSP source is not in the server catalog: ${catalogKey}`);
  }
  const [build, parmResult] = await Promise.all([
    dependencies.build(projectId, {
      model_key: catalogKey,
      resolution: options.resolution ?? 6,
      parm_overrides: Object.keys(parameterOverrides).length > 0 ? parameterOverrides : undefined,
    }, requestOptions),
    dependencies.fetchParms(projectId, catalogKey, requestOptions)
      .then((value) => ({ value, error: null }))
      .catch((error: unknown) => {
        if (isAbortError(error)) throw error;
        return { value: null, error };
      }),
  ]);
  const [document, surfacePayload, metadataPayload] = await Promise.all([
    dependencies.fetchModel(projectId, build.model_url, requestOptions),
    dependencies.fetchSurfaces(projectId, build.bezier_url, requestOptions),
    dependencies.fetchMetadata(projectId, build.metadata_url, requestOptions),
  ]);
  const components = decodeX57DisplayComponents(document, build.components);
  const surfaceModel = parseX57SurfaceModel(surfacePayload);
  const surfaceMetadata = parseX57SurfaceMetadata(metadataPayload);
  const source = requestedSource ?? sourceForCatalogKey(catalogKey) ?? X57_DEFAULT_VSP_SOURCE;
  const warnings = [
    "Actuator disks and propeller blades remain imported source references; they are not yet part of a conforming Loom Boolean assembly.",
  ];
  if (parmResult.error) warnings.push("Live VSP parameters were unavailable; geometry remains viewable but not editable.");
  const geoms = parmResult.value?.geoms ?? [];
  const editableParameters = selectX57MainWingParameters(geoms, source);
  return {
    delivery: "canonical-server-preview",
    modelName: X57_DEFAULT_MODEL_NAME,
    source,
    sourceLabel: `${source.label} · ${source.fileName}`,
    catalogKey,
    build,
    components,
    surfaceModel,
    surfaceMetadata,
    editableParameters,
    parameterOverrides,
    warnings,
  };
}
